
import type { SfxPlayer } from "../audio/sfx";
import { ChatChannel } from "../net/connection";
import { elem, installChrome, makeDraggable, makeResizable, styleInstaller } from "./chrome";
import { CursorShape, cursorCss } from "./cursor";
import { yieldToCandidateWindow } from "./ime";

/** Lines kept in the DOM. Beyond this the oldest is removed outright. */
const MAX_LINES = 60;

const LOG_MAX_LINES = 300;

/** How long a line stays at full opacity after it lands. */
const FADE_AFTER_MS = 5_000; // c_fStartDisappearingTime 5.0f (PythonChat.cpp:10)

/** Matches the server's `MAX_CHAT_CHARS`. Enforced here as a courtesy -- the
 *  server drops an overlong line and the player would never learn why. */
export const MAX_CHARS = 120;

/** The original calls this a stack of 32, then retains its thirty-third entry
 *  because it evicts only once the old stack is already larger (`uichat.py:323-335`). */
export const LAST_SENTENCE_STACK_SIZE = 32;

/** `CHAT_SHOUT_LIMIT` (`locale_game.txt:60`). */
export const SHOUT_LIMIT_MESSAGE = "You can only call every 15 seconds.";

export const SHOUT_COOLDOWN_MS = 15_000;

/** `CHAT_SEND_CHAT` (`locale_game.txt:57`) -- `btnSend`'s tooltip
 *  (`uichat.py:425`). */
export const SEND_CHAT_LABEL = "Send chat";

/** `CHAT_SEND_MEMO` (`locale_game.txt:58`) -- `btnSendWhisper`'s tooltip
 *  (`uichat.py:576`). */
export const SEND_WHISPER_LABEL = "Send whisper[Shift+Enter]";

/** `CHAT_LOG_WINDOW_MINIMUM_WIDTH` (`uichat.py:854`). */
export const CHAT_LOG_MINIMUM_WIDTH = 450;
/** `CHAT_LOG_WINDOW_MINIMUM_HEIGHT` (`uichat.py:855`). */
export const CHAT_LOG_MINIMUM_HEIGHT = 120;

/** `btnSizing.SetSize(16, 16)` (`uichat.py:942`) -- the corner grip's own
 *  square. The source loads no visual onto it (`:939-943`): it is a hit area
 *  in the corner, and the cursor is the whole of what the player sees. */
export const RESIZE_GRIP_SIZE = 16;

/** `self.btnChatSizing.SetSize(width, 22)` (`uichat.py:718`). */
export const SIZING_BAR_HEIGHT = 22;

/** The live transcript's floor: the bar stops at the chat window's own top and
 *  the text area closes (`uichat.py:763-765`). */
export const CHAT_FEED_MINIMUM_HEIGHT = 0;

export type ChatKind =
  | "say"
  | "system"
  | "notice"
  | "notice-big"
  | "party"
  | "guild"
  | "shout"
  | "whisper"
  | "whisper-sent";

/** The four a player can type into, in the order Tab cycles them
 *  (`uichat.py:145-165`). */
export const MODES = [
  ChatChannel.SAY,
  ChatChannel.PARTY,
  ChatChannel.GUILD,
  ChatChannel.SHOUT,
] as const;

/** The prefix each sticky mode pre-fills, mirroring `__ResetChat`
 *  (`uichat.py:194-205`). Empty for the area channel, which has none. */
const PREFIX: Record<number, string> = {
  [ChatChannel.SAY]: "",
  [ChatChannel.PARTY]: "#",
  [ChatChannel.GUILD]: "%",
  [ChatChannel.SHOUT]: "!",
};

// `CHAT_MODE_NAME` (`uichat.py:115-118`), the shipped mode-button words:
// `CHAT_NORMAL`/`CHAT_PARTY`/`CHAT_GUILD`/`CHAT_SHOUT` (`locale_game.txt:54,56,
// 49,59`) -- "Normal", "Group" and "Call", never "Nearby", "Party" or "Shout".
const MODE_LABEL: Record<number, string> = {
  [ChatChannel.SAY]: "Normal",
  [ChatChannel.PARTY]: "Group",
  [ChatChannel.GUILD]: "Guild",
  [ChatChannel.SHOUT]: "Call",
};

export function kindOfChannel(channel: number): ChatKind {
  switch (channel) {
    case ChatChannel.PARTY:
      return "party";
    case ChatChannel.GUILD:
      return "guild";
    case ChatChannel.SHOUT:
      return "shout";
    case ChatChannel.WHISPER:
      return "whisper";
    case ChatChannel.INFO:
      return "system";
    case ChatChannel.NOTICE:
      return "notice";
    default:
      return "say";
  }
}

export interface ChatLine {
  readonly kind: ChatKind;
  /** Empty for a system line, which has no speaker. */
  readonly from: string;
  readonly text: string;
}

export const LOG_TABS: readonly { readonly kind: ChatKind; readonly label: string }[] = [
  { kind: "say", label: MODE_LABEL[ChatChannel.SAY] },
  { kind: "party", label: MODE_LABEL[ChatChannel.PARTY] },
  { kind: "guild", label: MODE_LABEL[ChatChannel.GUILD] },
  { kind: "shout", label: MODE_LABEL[ChatChannel.SHOUT] },
  { kind: "system", label: "Info" },
  { kind: "notice", label: "Notice" },
];

/** "All", or the set of kinds a player has narrowed the log to. */
export type LogFilter = "all" | ReadonlySet<ChatKind>;

export function showsInLog(filter: LogFilter, kind: ChatKind): boolean {
  if (kind === "whisper" || kind === "whisper-sent" || kind === "notice-big") return true;
  return filter === "all" || filter.has(kind);
}

export function toggleLogFilter(filter: LogFilter, kind: ChatKind | "all"): LogFilter {
  if (kind === "all") return "all";
  if (filter === "all") return new Set([kind]);
  const next = new Set(filter);
  if (next.has(kind)) next.delete(kind);
  else next.add(kind);
  return next;
}

/** What the player asked to send, once the prefix has been read off. */
export interface Outgoing {
  /** A `ChatChannel`. `WHISPER` is the one that is a different message. */
  readonly channel: number;
  readonly text: string;
  /** The character name to reach. Non-empty only for a whisper. */
  readonly whisperTo: string;
}

/** Trim and cap, the same way the server will. Exported because the rule that
 *  the client and the server disagree about is the rule that loses a message. */
/** What sits between a speaker and what they said: space, colon, space.
 *  `"%s : %s"` (`input_main.cpp:683`). */
export const NAME_SEPARATOR = " : ";

export function normalise(text: string): string {
  return text.trim().slice(0, MAX_CHARS);
}

export function sendable(text: string): boolean {
  const trimmed = normalise(text);
  if (trimmed.length === 0) return false;
  for (const character of trimmed) {
    const code = character.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) return false;
  }
  return true;
}

export function parse(raw: string, mode: number = ChatChannel.SAY): Outgoing | null {
  const line = raw.trim();
  if (line.length === 0) return null;

  const whisper = /^\/(?:w|whisper)\s+(\S+)\s+([\s\S]+)$/.exec(line);
  if (whisper !== null) {
    const text = normalise(whisper[2]);
    if (!sendable(text)) return null;
    return { channel: ChatChannel.WHISPER, text, whisperTo: whisper[1] };
  }
  // A `/w` that did not match the shape above is a mistyped whisper, not a line
  // to broadcast. Sending it would put a half-typed name in front of everyone
  // standing nearby, which is the one failure mode a whisper must not have.
  if (/^\/(?:w|whisper)\b/.test(line)) return null;

  for (const channel of MODES) {
    const prefix = PREFIX[channel];
    if (prefix.length > 0 && line.startsWith(prefix)) {
      const text = normalise(line.slice(prefix.length));
      return sendable(text) ? { channel, text, whisperTo: "" } : null;
    }
  }

  const text = normalise(line);
  return sendable(text) ? { channel: mode, text, whisperTo: "" } : null;
}

export interface ChatOptions {
  readonly mount: HTMLElement;
  /** Called with a line the player wants to send. Already parsed and trimmed. */
  readonly onSend: (line: Outgoing) => void;
  /** Injected in tests. */
  readonly now?: () => number;
  /** `type` on every keystroke -- `OnIMEUpdate` (`ui.py:687`), one clip per
   *  `input` event on this build's plain (non-IME) text entry. Optional so a
   *  test fixture built without one stays silent. */
  readonly sfx?: SfxPlayer;
}

const STYLE_ID = "chat-channel-style";

const STYLE = `
.chat-say { color: rgb(255, 255, 255); }
.chat-party { color: rgb(0, 255, 228); }
.chat-guild { color: rgb(253, 255, 124); }
.chat-shout { color: rgb(167, 255, 212); }
.chat-whisper, .chat-whisper-sent { color: rgb(74, 225, 74); }
.chat-whisper-sent .chat-who::before { content: "\\2192\\00a0"; }
/* CHAT_RGB_INFO / CHAT_RGB_NOTICE (colorinfo.py:2-3). */
.chat-system { color: rgb(255, 200, 200); font-style: italic; }
.chat-notice { color: rgb(255, 230, 186); }
.chat-notice-big { color: rgb(255, 230, 186); font-weight: 600; }
.chat-mode {
  /* ChatModeButton draws its own outlined round box and brightens on hover
     (uichat.py:97-110); it is a button, and clicking it cycles the channel
     (uichat.py:400-406). */
  align-self: flex-start;
  padding: 1px 7px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 6px;
  background: rgba(12, 14, 18, 0.72);
  font: inherit;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9fb3c8;
  cursor: pointer;
}
.chat-mode:hover { background: rgba(255, 255, 255, 0.18); }
/*
 * The board the transcript is read against.
 *
 * ChatWindow.OnRender draws it before the lines and only in view mode:
 *
 *     grp.RenderGradationBar(self.xBar, ..., self.widthBar, self.curHeightBar,
 *                            self.BOARD_START_COLOR, self.BOARD_END_COLOR)
 *     chat.Render(self.chatID)                              uichat.py:812-815
 *
 * with BOARD_START_COLOR = GenerateColor(0, 0, 0, 0.0) and
 * BOARD_END_COLOR = GenerateColor(0, 0, 0, 0.8) (uichat.py:499-500) -- a
 * bar that is clear at the top and 80% black at the foot. This build drew the
 * lines on bare terrain instead and said so in style.css ("no background:
 * the feed is text over the world"); over the plaza's pale paving that is the
 * unreadable chat of parity.json's last row. The gradient is the
 * original's, so the world still shows through the top of the strip and the
 * text still sits on something dark.
 *
 * RefreshBoardViewState sizes the bar to visibleLineCount * lineStep and
 * adds its padding only when there is a line to show (uichat.py:767-790), so
 * an empty transcript draws no bar at all.
 */
.chat-feed { background: linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.8)); }
.chat-feed:empty { background: none; }
/*
 * The fade is the line's, not the window's.
 *
 * CPythonChat::UpdateViewMode decays rColor.a on each chat line past
 * c_fStartDisappearingTime (PythonChat.cpp:113-124); BOARD_START_COLOR
 * and BOARD_END_COLOR are constants and the bar is drawn at them on every
 * frame regardless. Fading .chat as a whole took the backing down with the
 * text, which is what left a quiet line at 28% white over open ground with
 * nothing behind it. .chat-form and .chat-mode fade with the lines because
 * the source hides that whole row when the input set is closed
 * (ChatInputSet.Open/Close, uichat.py:434-455).
 */
.chat-quiet .chat-line,
.chat-quiet .chat-form,
.chat-quiet .chat-mode { opacity: 0.28; }
/* ChatLogWindow minimums (uichat.py:854-855); m2-board supplies the frame.
   resize: none because this window has the source's own grip in its corner
   now (.chat-log-grip below), and two handles in one corner is one more than
   the window has. The minimums are the grip's own clamp as well
   (CHAT_LOG_MINIMUM_WIDTH/_HEIGHT), so a drag and the stylesheet stop in the
   same place. */
.chat-log.m2-board {
  min-width: 450px;
  min-height: 120px;
  width: min(450px, calc(100vw - 32px));
  border-radius: 0;
  /* ChatLogWindow.OnRender fills its rect with 0x77000000 (uichat.py:1125-1133). */
  background: rgba(0, 0, 0, ${0x77 / 255});
  box-shadow: none;
  resize: none;
}
/*
 * ChatLogWindow.ResizeButton (uichat.py:857-869, 939-943): 16x16, parked in
 * the bottom-right corner, with no visual of its own -- the source sets none,
 * so what tells a player it is there is the HVSIZE cursor and nothing else.
 * That is kept: an invisible square, and the pointer changes over it.
 */
.chat-log-grip {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 16px;
  height: 16px;
  touch-action: none;
}
/*
 * ChatWindow.ChatButton (uichat.py:513-543): the sizing bar over the
 * transcript, 22 tall and as wide as the window (__RefreshSizingBar,
 * :714-722), drawn in the source from chat_bar_left/_middle/_right.tga
 * (:596-620). Those three tiles are not among the rasters
 * tools/import_game_ui.py brings across, so the strip is flat at the same
 * height, with the same VSIZE cursor over it.
 *
 * .chat-feed carries resize: vertical in style.css -- the browser's own
 * handle, in the wrong corner for a bar that lives on the top edge -- and it
 * is switched off here for the same reason the log's is.
 */
.chat-sizing-bar {
  height: 22px;
  flex: 0 0 auto;
  border-top: 1px solid rgba(255, 255, 255, 0.35);
  border-bottom: 1px solid rgba(0, 0, 0, 0.6);
  background: linear-gradient(to bottom, rgba(90, 96, 108, 0.85), rgba(28, 32, 40, 0.85));
  touch-action: none;
}
.chat-sizing-bar[hidden] { display: none; }
.chat .chat-feed { resize: none; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export class ChatBox {
  private readonly root: HTMLElement;
  private readonly feed: HTMLElement;
  /** `ChatModeButton` (`uichat.py:38-110`): a button, not a caption. */
  private readonly badge: HTMLButtonElement;
  private readonly input: HTMLInputElement;
  /** `btnSend` (`uichat.py:420-427`) and `btnSendWhisper` (`:571-578`), both
   *  shown only while the input set is open, like `btnChatLog`. */
  private readonly sendButton: HTMLButtonElement;
  private readonly whisperButton: HTMLButtonElement;
  /** When this box last let a shout through, for `SHOUT_COOLDOWN_MS`.
   *  `lastShoutTime` (`uichat.py:123, 247-255`). */
  private lastShoutAt = Number.NEGATIVE_INFINITY;
  private readonly now: () => number;
  private readonly stamps: number[] = [];
  private readonly lastSentences: string[] = [];
  private lastSentencePosition = 0;

  /** `btnChatLog`: hidden until the player is typing, shown by `OpenChat`
   *  and hidden again by `CloseChat` (`uichat.py:580-587, 672-698`). */
  private readonly logButton: HTMLButtonElement;
  /** `btnChatSizing` (`uichat.py:509-543`) and `btnSizing` (`:857-869`): the
   *  live board's sizing bar and the log window's corner grip. */
  private readonly sizingBar: HTMLElement;
  private readonly logGrip: HTMLElement;
  private readonly logPanel: HTMLElement;
  private readonly logTabsEl: HTMLElement;
  private readonly logFeed: HTMLElement;
  /** Every line this session has seen, capped independently of the live
   *  board -- see `LOG_MAX_LINES`. */
  private readonly history: ChatLine[] = [];
  private logFilter: LogFilter = "all";
  /** `systemSetting.IsViewChat()`: hide the idle board, not the ability to
   * start typing.  The original renders it again while the board is edited
   * (`uichat.py:793-815`). */
  private viewChat = true;

  /** The sticky channel, cycled with Tab. Starts on the area around you, which
   *  is the one channel that always exists. */
  private mode: number = ChatChannel.SAY;

  constructor(options: ChatOptions) {
    this.now = options.now ?? (() => performance.now());
    installStyle(options.mount.ownerDocument);

    this.root = elem(document, "div", "chat");

    this.feed = elem(document, "div", "chat-feed");
    // A live region, so a screen reader announces arrivals rather than leaving
    // the player to poll a box they cannot see.
    this.feed.setAttribute("role", "log");
    this.feed.setAttribute("aria-live", "polite");

    this.badge = document.createElement("button");
    this.badge.type = "button";
    this.badge.className = "chat-mode";
    this.badge.addEventListener("mousedown", (event) => event.preventDefault());
    this.badge.addEventListener("click", () => {
      this.cycleMode();
      this.input.focus();
    });

    this.logButton = document.createElement("button");
    this.logButton.type = "button";
    this.logButton.className = "chat-log-button";
    this.logButton.hidden = true;
    // `CHAT_LOG` (`locale_game.txt:52`): the tooltip on the button that opens
    // the log, not a live label -- the button itself carries no text in the
    // original either.
    this.logButton.title = "Show old messages [L]";
    this.logButton.setAttribute("aria-label", "Show old messages");
    this.logButton.textContent = "Log";
    // Without this the mousedown blurs the input, `refreshVisibility` hides
    // this very button, and the click never lands on anything. The same trap
    // is why the two buttons below take it too.
    this.logButton.addEventListener("mousedown", (event) => event.preventDefault());
    this.logButton.addEventListener("click", () => this.toggleLog());

    this.whisperButton = document.createElement("button");
    this.whisperButton.type = "button";
    this.whisperButton.className = "chat-whisper-button";
    this.whisperButton.hidden = true;
    this.whisperButton.title = SEND_WHISPER_LABEL;
    this.whisperButton.setAttribute("aria-label", SEND_WHISPER_LABEL);
    this.whisperButton.textContent = "W";
    this.whisperButton.addEventListener("mousedown", (event) => event.preventDefault());
    this.whisperButton.addEventListener("click", () => this.prefill("/w "));

    const controls = elem(document, "div", "chat-controls");
    controls.append(this.badge, this.whisperButton, this.logButton);

    const form = elem(document, "form", "chat-form");

    this.input = document.createElement("input");
    this.input.type = "text";
    this.input.className = "chat-input";
    // One over the server's cap, so a prefix does not eat a character of the
    // message: the prefix is stripped before the length that matters is
    // measured, and `normalise` cuts to `MAX_CHARS` regardless.
    this.input.maxLength = MAX_CHARS + 1;
    this.input.setAttribute("aria-label", "Chat message");
    this.input.autocomplete = "off";

    this.sendButton = document.createElement("button");
    this.sendButton.type = "submit";
    this.sendButton.className = "chat-send-button";
    this.sendButton.hidden = true;
    this.sendButton.title = SEND_CHAT_LABEL;
    this.sendButton.setAttribute("aria-label", SEND_CHAT_LABEL);
    this.sendButton.textContent = "\u27a4";
    this.sendButton.addEventListener("mousedown", (event) => event.preventDefault());

    form.append(this.input, this.sendButton);

    this.sizingBar = elem(document, "div", "chat-sizing-bar");
    this.sizingBar.hidden = true;
    this.sizingBar.setAttribute("aria-hidden", "true");
    makeResizable(this.feed, this.sizingBar, {
      // `restrict_x` (`uichat.py:519`): the bar moves only vertically, and it
      // sits on the *top* edge of a strip anchored at the foot of the screen,
      // so a drag upward is what makes the transcript taller
      // (`RefreshBoardEditState`, `:744-765`).
      growX: 0,
      growY: -1,
      minHeight: CHAT_FEED_MINIMUM_HEIGHT,
      cursor: cursorCss(CursorShape.VSIZE),
      // `chat.ArrangeShowingChat` after every `SetHeight` (`uichat.py:761`):
      // the newly sized box shows the newest lines, not whatever the old
      // scroll offset happened to be looking at.
      onResize: () => {
        this.feed.scrollTop = this.feed.scrollHeight;
      },
    });

    this.root.append(this.sizingBar, this.feed, controls, form);
    options.mount.append(this.root);
    this.showMode();

    installChrome(options.mount.ownerDocument);
    this.logPanel = elem(document, "section", "chat-log m2-board");
    this.logPanel.hidden = true;
    this.logPanel.setAttribute("role", "dialog");
    this.logPanel.setAttribute("aria-label", "Chat log");

    const logHeader = elem(document, "div", "chat-log-header");
    const logTitle = elem(document, "span", "chat-log-title");
    // `CHAT_LOG_TITLE` (`locale_game.txt:53`).
    logTitle.textContent = "Chat-log";
    const logClose = document.createElement("button");
    logClose.type = "button";
    logClose.className = "chat-log-close";
    logClose.textContent = "×";
    logClose.setAttribute("aria-label", "Close chat log");
    logClose.addEventListener("click", () => this.closeLog());
    logHeader.append(logTitle, logClose);

    this.logTabsEl = elem(document, "div", "chat-log-tabs");

    this.logFeed = elem(document, "div", "chat-log-feed");
    this.logFeed.setAttribute("role", "log");
    this.logFeed.setAttribute("aria-live", "off");

    // `ResizeButton` (`uichat.py:857-869`), built and sized at `:939-943` and
    // parked at the window's bottom-right corner (`:892`). `OnResize`'s two
    // clamps are the minimums below, and they are why a player cannot drag the
    // tab row out of existence (`:1073-1086`).
    this.logGrip = elem(document, "div", "chat-log-grip");
    this.logGrip.setAttribute("aria-hidden", "true");
    this.logPanel.append(logHeader, this.logTabsEl, this.logFeed, this.logGrip);
    options.mount.append(this.logPanel);
    this.buildLogTabs();
    makeDraggable(this.logPanel, logHeader);
    makeResizable(this.logPanel, this.logGrip, {
      minWidth: CHAT_LOG_MINIMUM_WIDTH,
      minHeight: CHAT_LOG_MINIMUM_HEIGHT,
      anchor: true,
      cursor: cursorCss(CursorShape.HVSIZE),
      // `OnResize` re-lays the window's contents after every drag -- the input
      // set's width and character cap, the scroll bar's own length
      // (`uichat.py:1088-1094`). Here the flex column does the laying out, and
      // the one thing it cannot do by itself is stay on the newest line.
      onResize: () => {
        this.logFeed.scrollTop = this.logFeed.scrollHeight;
      },
    });

    options.mount.ownerDocument.addEventListener("keydown", (event) => {
      if (!this.logPanel.isConnected || event.key !== "Escape" || !this.logShowing) return;
      event.preventDefault();
      event.stopPropagation();
      this.closeLog();
    });

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const typed = this.input.value;
      // The client remembers every non-empty submitted draft before deciding
      // whether it is sendable (`uichat.py:323-348`). This includes a channel
      // mark a player abandoned, so Up restores exactly what they were editing.
      this.pushLastSentence(typed);
      const line = parse(typed, this.mode);
      this.input.value = "";
      this.lastSentencePosition = 0;
      if (line !== null && line.channel === ChatChannel.SHOUT && !this.shoutReady()) {
        // `__SendShoutChatPacket` refuses, says CHAT_SHOUT_LIMIT on the INFO
        // channel and resets the editor without ever calling
        // `__SendChatPacket` (`uichat.py:241-255`).
        this.push({ kind: "system", from: "", text: SHOUT_LIMIT_MESSAGE });
      } else if (line !== null) {
        if (line.channel === ChatChannel.SHOUT) this.lastShoutAt = this.now();
        options.onSend(line);
      }
      // Blurred on send: the next keypress should move the character, which is
      // what a player who just finished typing expects.
      this.input.blur();
    });

    this.input.addEventListener("keydown", (event) => {
      // Stopped here rather than checking `focused` in the keyboard handler as
      // well; both would work and only one of them can be forgotten.
      event.stopPropagation();
      // Escape, the arrows and Enter belong to the candidate window while an
      // input method is composing; the editor's own bindings for them come
      // back the moment it commits.
      if (yieldToCandidateWindow(event)) return;
      if (event.key === "Escape") {
        // The legacy input returns True after clearing and firing its escape
        // callback (`uichat.py:363-366`), so the browser must consume the
        // default as well as stopping the game listener.
        event.preventDefault();
        this.input.value = "";
        this.lastSentencePosition = 0;
        this.input.blur();
        return;
      }
      if (event.key === "ArrowUp") {
        // `OnIMEKeyDown` consumes Up after moving one entry toward the oldest
        // submitted sentence (`uichat.py:287-310`).
        event.preventDefault();
        this.previousLastSentence();
        return;
      }
      if (event.key === "ArrowDown") {
        // Down reverses only as far as the most recent entry; it deliberately
        // does not clear the editor after that (`uichat.py:312-321`).
        event.preventDefault();
        this.nextLastSentence();
        return;
      }
      if (event.key === "Tab") {
        // The original binds this to a button and to the same cycle
        // (`uichat.py:145-165`). Prevented rather than allowed through, because
        // the default would move focus out of the box mid-sentence.
        event.preventDefault();
        this.cycleMode();
      }
    });
    this.input.addEventListener("blur", () => this.refreshVisibility());
    // `OnIMEUpdate` (`ui.py:687`) fires on every update to the IME's text
    // buffer; the native `input` event is the same fact one-for-one on this
    // build's plain text entry, which has no IME composition layer to
    // distinguish a buffer update from a committed keystroke.
    this.input.addEventListener("input", () => options.sfx?.play("type"));
  }

  /** True while the player is typing. The keyboard handler must stand down. */
  get focused(): boolean {
    return document.activeElement === this.input;
  }

  /** The sticky channel a bare line would go to. For tests and for the caller
   *  that wants to say so. */
  get channel(): number {
    return this.mode;
  }

  setViewChat(visible: boolean): void {
    this.viewChat = visible;
    this.refreshVisibility();
  }

  focus(): void {
    // Unhide before focusing: a hidden input cannot acquire focus in Safari.
    this.root.hidden = false;
    this.resetDraftForMode();
    this.input.focus();
    this.refreshVisibility();
  }

  cycleMode(): void {
    const at = MODES.indexOf(this.mode as (typeof MODES)[number]);
    this.mode = MODES[(at + 1) % MODES.length];
    this.input.value = PREFIX[this.mode];
    this.showMode();
    // The caret goes after the prefix, or the next keystroke lands in front of
    // it and the line is sent to the wrong channel.
    this.input.setSelectionRange(this.input.value.length, this.input.value.length);
  }

  /** Recall an older submitted draft, without sending it. */
  private previousLastSentence(): void {
    if (this.lastSentencePosition >= this.lastSentences.length) return;
    this.lastSentencePosition += 1;
    this.setDraft(this.lastSentences[this.lastSentences.length - this.lastSentencePosition] ?? "");
  }

  /** Move toward the newest submitted draft. The native client stops there
   *  rather than inventing an empty post-history position. */
  private nextLastSentence(): void {
    if (this.lastSentencePosition <= 1) return;
    this.lastSentencePosition -= 1;
    this.setDraft(this.lastSentences[this.lastSentences.length - this.lastSentencePosition] ?? "");
  }

  private pushLastSentence(text: string): void {
    if (text.length === 0) return;
    if (this.lastSentences.length > LAST_SENTENCE_STACK_SIZE) this.lastSentences.shift();
    this.lastSentences.push(text);
  }

  private setDraft(text: string): void {
    this.input.value = text;
    this.input.setSelectionRange(text.length, text.length);
  }

  /** The native `__ResetChat`: the talking mode clears the history cursor;
   *  sticky marked modes leave that cursor alone while drawing their mark
   *  (`uichat.py:191-208`). */
  private resetDraftForMode(): void {
    const prefix = PREFIX[this.mode];
    this.setDraft(prefix);
    if (this.mode === ChatChannel.SAY) this.lastSentencePosition = 0;
  }

  /** `ime.PasteString` -- put `text` in at the caret and leave the caret after
   *  it, replacing whatever was selected. What Alt+clicking an item does with
   *  the link it builds (`uidragonsoul.py:370-372`, `uiinventory.py:764-766`). */
  paste(text: string): void {
    const at = this.input.selectionStart ?? this.input.value.length;
    const to = this.input.selectionEnd ?? at;
    const before = this.input.value.slice(0, at);
    const after = this.input.value.slice(to);
    this.input.value = before + text + after;
    this.input.focus();
    const caret = before.length + text.length;
    this.input.setSelectionRange(caret, caret);
  }

  prefill(text: string): void {
    this.input.value = text;
    this.input.focus();
    this.input.setSelectionRange(this.input.value.length, this.input.value.length);
  }

  /** Build one line's markup. Shared by the live board and the log, so the
   *  one rule that matters -- text goes in through `textContent`, always --
   *  has exactly one place it could be broken. */
  private buildLine(line: ChatLine): HTMLElement {
    const element = elem(document, "p", `chat-line chat-${line.kind}`);

    if (line.from.length > 0) {
      const who = elem(document, "span", "chat-who", `${line.from}${NAME_SEPARATOR}`);
      element.append(who);
    }

    const body = elem(document, "span", "chat-text", line.text);
    element.append(body);
    return element;
  }

  /** Add a line. Text is set, never parsed. */
  push(line: ChatLine): void {
    this.feed.append(this.buildLine(line));
    this.stamps.push(this.now());

    while (this.feed.childElementCount > MAX_LINES) {
      this.feed.firstElementChild?.remove();
      this.stamps.shift();
    }

    // Newest at the bottom, and the view follows it. Without this the feed
    // silently stops updating the moment it is taller than its box.
    this.feed.scrollTop = this.feed.scrollHeight;

    // The log keeps its own, longer memory of the same line
    // (`chat.AppendChat` fans one line out to every registered chat set,
    // `uichat.py:625-641`'s colour table registers both), independent of
    // whatever the live board has already trimmed.
    this.history.push(line);
    if (this.history.length > LOG_MAX_LINES) this.history.shift();
    if (this.logShowing) this.renderLog();
  }

  update(): void {
    const last = this.stamps[this.stamps.length - 1];
    const quiet =
      !this.focused && (last === undefined || this.now() - last >= FADE_AFTER_MS); // PythonChat.cpp:114
    this.root.classList.toggle("chat-quiet", quiet);
  }

  /** How many lines are currently in the DOM. For tests. */
  get lineCount(): number {
    return this.feed.childElementCount;
  }

  private showMode(): void {
    this.badge.textContent = MODE_LABEL[this.mode];
    this.badge.className = `chat-mode chat-${kindOfChannel(this.mode)}`;
    this.input.placeholder = "";
  }

  private refreshVisibility(): void {
    this.root.hidden = !this.viewChat && !this.focused;
    this.logButton.hidden = !this.focused;
    this.whisperButton.hidden = !this.focused;
    this.sendButton.hidden = !this.focused;
    // `btnChatSizing` belongs to that same set: `OpenChat` shows it and
    // `CloseChat` hides it (`uichat.py:667-668, 696`). It is also what makes a
    // transcript a player has dragged shut recoverable -- pressing Enter puts
    // the bar back on screen to be pulled open again.
    this.sizingBar.hidden = !this.focused;
  }

  /** Whether this box will let a shout through, per `SHOUT_COOLDOWN_MS`.
   *  `app.GetTime() < self.lastShoutTime + 15` (`uichat.py:247`). */
  private shoutReady(): boolean {
    return this.now() - this.lastShoutAt >= SHOUT_COOLDOWN_MS;
  }

  /** Whether the chat log is currently on screen. */
  get logShowing(): boolean {
    return !this.logPanel.hidden;
  }

  /** Open the log (`ChatLogWindow.Open`, `uichat.py:1063-1067`) and draw it
   *  against whatever has already been said this session. */
  openLog(): void {
    this.logPanel.hidden = false;
    this.renderLog();
  }

  /** Close the log without touching the live board -- the two are separate
   *  windows and neither one's visibility depends on the other's. */
  closeLog(): void {
    this.logPanel.hidden = true;
  }

  toggleLog(): void {
    if (this.logShowing) this.closeLog();
    else this.openLog();
  }

  /** For tests: the filter the log is currently drawn under. */
  get filter(): LogFilter {
    return this.logFilter;
  }

  private setLogFilter(kind: ChatKind | "all"): void {
    this.logFilter = toggleLogFilter(this.logFilter, kind);
    this.buildLogTabs();
    this.renderLog();
  }

  private buildLogTabs(): void {
    this.logTabsEl.replaceChildren();

    const all = document.createElement("button");
    all.type = "button";
    all.className = `chat-log-tab${this.logFilter === "all" ? " chat-log-tab-active" : ""}`;
    // `CHAT_ALL` (`locale_game.txt:47`).
    all.textContent = "All";
    all.addEventListener("click", () => this.setLogFilter("all"));
    this.logTabsEl.append(all);

    for (const tab of LOG_TABS) {
      const button = document.createElement("button");
      button.type = "button";
      const active = this.logFilter !== "all" && this.logFilter.has(tab.kind);
      button.className = `chat-log-tab chat-${tab.kind}${active ? " chat-log-tab-active" : ""}`;
      button.textContent = tab.label;
      button.addEventListener("click", () => this.setLogFilter(tab.kind));
      this.logTabsEl.append(button);
    }
  }

  private renderLog(): void {
    this.logFeed.replaceChildren(
      ...this.history
        .filter((line) => showsInLog(this.logFilter, line.kind))
        .map((line) => this.buildLine(line)),
    );
    this.logFeed.scrollTop = this.logFeed.scrollHeight;
  }
}
